import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Megaphone,
  Newspaper,
  QrCode,
  ShieldOff,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Building2,
  Briefcase,
  Users,
  User,
} from 'lucide-react';
import AppLayout from '@components/AppLayout';
import client from '../../src/api/client';
import '../styles/dashboard.css';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';

const getInitials = (firstName = '', lastName = '') => {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();
  return initials || 'LC';
};

const getUnionAcronymFromDigitalId = (digitalId) => {
  if (!digitalId) return null;
  const parts = String(digitalId)
    .split('-')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) return null;
  return parts[1].toUpperCase();
};

const resolveMediaUrl = (value) => {
  return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
};

const normalizeNewsItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const startAt = item.startAt ?? item.start_at ?? item.publishedAt ?? item.published_at ?? null;
  const endAt = item.endAt ?? item.end_at ?? null;
  const heroImage = resolveMediaUrl(item.heroImage ?? item.previewImage ?? item.imagePath ?? item.image);

  return {
    ...item,
    startAt,
    endAt,
    publishedAt: item.publishedAt ?? item.published_at ?? startAt,
    heroImage,
  };
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatMemberSince = (value) => {
  if (!value) return 'Not provided';
  const raw = String(value);
  const date = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Not provided';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
};

const QUICK_ACTIONS = [
  {
    label: 'Digital ID',
    description: 'View Digital Union ID',
    icon: QrCode,
    color: 'blue',
    path: '/digital-id',
  },
  {
    label: 'Request and assistance',
    description: 'Submit tickets & get help',
    icon: Megaphone,
    color: 'yellow',
    path: '/request-assistance',
  },
  {
    label: 'Book Venue',
    description: 'Reserve venue for events',
    icon: Calendar,
    color: 'rose',
    path: '/venues',
  },
  {
    label: 'Events',
    description: 'View upcoming events',
    icon: Newspaper,
    color: 'teal',
    path: '/news',
  },
];

const EVENT_BADGE_TONE_MAP = {
  Assembly: 'blue',
  Training: 'purple',
  Outreach: 'orange',
  Benefit: 'green',
  Other: 'slate',
  'Union Update': 'orange',
  Events: 'blue',
  Learning: 'purple',
  Benefits: 'green',
};

const DASHBOARD_NEWS_CACHE_KEY = 'alu_dashboard_news_cache_v1';
const DASHBOARD_NEWS_CACHE_TTL_MS = 60 * 1000;

const getEventBadgeTone = (category) => EVENT_BADGE_TONE_MAP[category] ?? 'slate';

export default function DashboardPage({ user, dues, onLogout }) {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const syncViewport = () => {
      setIsMobileView(mediaQuery.matches);
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    let hydratedFromCache = false;

    try {
      const raw = sessionStorage.getItem(DASHBOARD_NEWS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const cachedAt = Number(parsed?.cachedAt ?? 0);
        const cachedItems = Array.isArray(parsed?.items) ? parsed.items : null;
        if (cachedItems && cachedAt > 0 && (Date.now() - cachedAt) <= DASHBOARD_NEWS_CACHE_TTL_MS) {
          const normalizedCached = cachedItems.map(normalizeNewsItem).filter(Boolean);
          setNewsItems(normalizedCached);
          hydratedFromCache = true;
        }
      }
    } catch {
      // Ignore malformed cache payloads.
    }

    const fetchNews = async () => {
      try {
        const { data } = await client.get('/api/news');
        const normalized = Array.isArray(data)
          ? data.map(normalizeNewsItem).filter(Boolean)
          : [];
        setNewsItems(normalized);

        try {
          sessionStorage.setItem(DASHBOARD_NEWS_CACHE_KEY, JSON.stringify({
            cachedAt: Date.now(),
            items: normalized,
          }));
        } catch {
          // Ignore cache write failures.
        }
      } catch (error) {
        console.error('Error fetching news:', error);
        if (!hydratedFromCache) {
          setNewsItems([]);
        }
      }
    };
    fetchNews();
  }, []);

  const normalizedStatus = typeof user?.isApproved === 'string'
    ? user.isApproved.trim().toLowerCase()
    : user?.isApproved;

  const isVerified = normalizedStatus === 'active'
    || normalizedStatus === 'approved'
    || normalizedStatus === true
    || normalizedStatus === 1
    || normalizedStatus === '1';

  const isIncomplete = normalizedStatus === 'incomplete';
  const isRejected = normalizedStatus === 'rejected';
  const shouldMaskUnionInfo = isIncomplete || (!isVerified && !isRejected);
  const maskedUnionValue = '_ _';

  const statusChipClassName = `dashboard-page__status-chip${
    isVerified ? '' : isRejected
      ? ' dashboard-page__status-chip--rejected'
      : ' dashboard-page__status-chip--pending'
  }`;

  const statusChipLabel = isRejected
    ? (isMobileView ? 'Rejected' : 'Application Rejected')
    : isVerified
      ? (isMobileView ? 'Verified' : 'Verified Member')
      : isIncomplete
        ? 'Incomplete'
        : isMobileView
          ? 'Pending'
          : 'Pending Approval';

  const StatusIcon = isRejected || !isVerified ? AlertCircle : CheckCircle;

  const unionAcronym = useMemo(
    () => getUnionAcronymFromDigitalId(user.digitalId),
    [user.digitalId],
  );

  const resolvedProfilePicture = useMemo(
    () => resolveBackendAssetUrl(user.profilePicture, { apiBaseUrl: API_BASE_URL }),
    [user.profilePicture],
  );

  // Filter news for events or just take the latest news items
  const upcomingEvents = useMemo(() => {
    if (!newsItems || newsItems.length === 0) return [];
    // Prioritize items with category 'Events', otherwise show all news
    const events = newsItems.filter(item => item.category === 'Events');
    return events.length > 0 ? events : newsItems;
  }, [newsItems]);

  const [currentEventPage, setCurrentEventPage] = useState(0);
  const eventsPerPage = 3;
  const totalEventPages = Math.max(1, Math.ceil(upcomingEvents.length / eventsPerPage));
  const shouldPaginateEvents = upcomingEvents.length > eventsPerPage;

  useEffect(() => {
    setCurrentEventPage((previousPage) => Math.min(previousPage, totalEventPages - 1));
  }, [totalEventPages]);

  const handlePrevEvent = () => {
    setCurrentEventPage((previousPage) => Math.max(previousPage - 1, 0));
  };

  const handleNextEvent = () => {
    setCurrentEventPage((previousPage) => Math.min(previousPage + 1, totalEventPages - 1));
  };

  const handleReapply = () => {
    navigate('/membership-form', { state: { isReapplication: true } });
  };

  const visibleEvents = upcomingEvents.slice(
    currentEventPage * eventsPerPage,
    (currentEventPage + 1) * eventsPerPage,
  );

  return (
    <AppLayout title="Dashboard" user={user} onLogout={onLogout}>
      <div className="dashboard-page">
        {isRejected && (
          <section className="dashboard-page__verification dashboard-page__verification--rejected">
            <div className="dashboard-page__verification-icon">
              <ShieldOff size={22} />
            </div>
            <div>
              <h3>Membership Rejected</h3>
              {user.rejectionReason ? (
                <div className="dashboard-page__rejection-detail">
                  <p>
                    <span className="dashboard-page__rejection-label">
                      Reason for Rejection:
                    </span>
                    {user.rejectionReason}
                  </p>
                </div>
              ) : (
                <p>Your membership application has been approved. Please review the reason.</p>
              )}
            </div>
            <div className="dashboard-page__verification-actions">
              <button type="button" className="button button--secondary" onClick={() => navigate('/membership-form')}>
                Review Application
              </button>
              <button type="button" className="button button--primary" onClick={handleReapply}>
                Re-apply
              </button>
            </div>
          </section>
        )}

        <section className="dashboard-page__layout">
          <div className="dashboard-page__main-column">
            <div className="dashboard-page__welcome-stack">
              <div className="dashboard-page__welcome">
                <div className="dashboard-page__welcome-main">
                  <div className="dashboard-page__avatar">
                    {resolvedProfilePicture ? (
                      <img src={resolvedProfilePicture} alt={user.firstName} />
                    ) : (
                      <span>{getInitials(user.firstName, user.lastName)}</span>
                    )}
                  </div>
                  <div className="dashboard-page__welcome-identity">
                    <span className="dashboard-page__eyebrow">Welcome,</span>
                    <h2>{`${user.firstName || 'Member'} ${user.lastName || ''}`.trim()}</h2>
                  </div>
                </div>
              </div>

              <div className="dashboard-page__welcome-actions">
                {!isVerified ? (
                  <>
                    {isIncomplete ? (
                      <button type="button" className="button--primary" onClick={() => navigate('/complete-profile')}>
                        Complete Verification
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="button--primary"
                        onClick={() => navigate('/membership-form', { state: { viewStatus: true } })}
                      >
                        View Application Status
                      </button>
                    )}
                    <button
                      type="button"
                      className="button--secondary dashboard-page__view-digital-id"
                      onClick={() => navigate('/digital-id')}
                    >
                      View Digital ID
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="dashboard-page__view-digital-id"
                      onClick={() => navigate('/digital-id')}
                    >
                      View Digital ID
                    </button>
                    {isRejected ? (
                      <button type="button" onClick={handleReapply}>
                        Re-apply
                      </button>
                    ) : (
                      <button type="button" className="dashboard-page__book-now" onClick={() => navigate('/venues')}>
                        Book Now
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="dashboard-page__quick dashboard-page__quick--overview">
              <h3>Quick access</h3>
              <div className="dashboard-page__quick-grid">
                {QUICK_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.label}
                      type="button"
                      className={`dashboard-page__quick-card dashboard-page__quick-card--${action.color}`}
                      onClick={() => navigate(action.path)}
                    >
                      <span>
                        <Icon size={18} />
                      </span>
                      <div>
                        <strong>{action.label}</strong>
                        <p>{action.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {!isRejected && (
              <div className="dashboard-page__events-wrap">
                <article className="dashboard-page__card">
                  <header>
                    <div>
                      <h4>Upcoming Events</h4>
                    </div>
                    {shouldPaginateEvents && (
                      <div className="dashboard-page__carousel-controls">
                        <button
                          type="button"
                          onClick={handlePrevEvent}
                          aria-label="Previous events"
                          disabled={currentEventPage === 0}
                        >
                          <ChevronLeft size={15} />
                        </button>
                        <span className="dashboard-page__carousel-page-indicator" aria-live="polite">
                          {currentEventPage + 1} / {totalEventPages}
                        </span>
                        <button
                          type="button"
                          onClick={handleNextEvent}
                          aria-label="Next events"
                          disabled={currentEventPage >= totalEventPages - 1}
                        >
                          <ChevronRight size={15} />
                        </button>
                      </div>
                    )}
                  </header>
                  <div className="dashboard-page__event-grid">
                    {upcomingEvents.length === 0 ? (
                      <div className="dashboard-page__empty">
                        <Calendar size={18} />
                        <p>No scheduled events yet. Check back soon.</p>
                      </div>
                    ) : (
                      visibleEvents.map((event) => (
                        <div key={event.id} className="dashboard-event-card" onClick={() => navigate('/news')}>
                          <div className="dashboard-event-card__image-container">
                            <img
                              src={event.heroImage || 'https://placehold.co/600x400?text=Event'}
                              alt={event.title}
                              className="dashboard-event-card__image"
                            />
                            <div className="dashboard-event-card__badges">
                              <span className={`dashboard-event-card__badge dashboard-event-card__badge--${getEventBadgeTone(event.category)}`}>
                                {event.category}
                              </span>
                            </div>
                          </div>
                          <div className="dashboard-event-card__content">
                            <h5>{event.title}</h5>
                            <p className="dashboard-event-card__desc">
                              {event.summary || event.content || 'Stay updated for more details about this event.'}
                            </p>
                            <div className="dashboard-event-card__meta">
                              <span>
                                <Calendar size={14} />
                                {formatDate(event.startAt || event.publishedAt)}
                              </span>
                              {event.venue && (
                                <span>
                                  <MapPin size={14} />
                                  {event.venue}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </article>
              </div>
            )}
          </div>

          <aside className="dashboard-page__union-info">
            <div className="dashboard-page__union-header">
              <h3>Union Information</h3>
              <div className="dashboard-page__union-status">
                <span className={statusChipClassName}>
                  <StatusIcon size={16} />
                  {statusChipLabel}
                </span>
              </div>
            </div>
            <div className="dashboard-page__union-list">
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <Building2 size={18} />
                </span>
                <div>
                  <small>Company</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : (user.company ?? 'Not provided')}</strong>
                </div>
              </div>
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <Briefcase size={18} />
                </span>
                <div>
                  <small>Position</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : (user.position ?? 'Not provided')}</strong>
                </div>
              </div>
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <Users size={18} />
                </span>
                <div>
                  <small>Union</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : (unionAcronym ?? user.unionName ?? user.unionAffiliation ?? user.union ?? 'Not provided')}</strong>
                </div>
              </div>
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <QrCode size={18} />
                </span>
                <div>
                  <small>Union ID</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : (user.membershipNo ?? user.membership_no ?? user.digitalId ?? 'Not provided')}</strong>
                </div>
              </div>
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <User size={18} />
                </span>
                <div>
                  <small>Union role</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : (user.unionPosition ?? 'Member')}</strong>
                </div>
              </div>
              <div className="dashboard-page__union-item">
                <span className="dashboard-page__union-icon" aria-hidden="true">
                  <Calendar size={18} />
                </span>
                <div>
                  <small>Member since</small>
                  <strong>{shouldMaskUnionInfo ? maskedUnionValue : formatMemberSince(user.membershipDate)}</strong>
                </div>
              </div>
            </div>
            <button type="button" className="dashboard-page__union-button" onClick={() => navigate('/account')}>
              Manage Union
            </button>
          </aside>

        </section>
      </div>
    </AppLayout>
  );
}

DashboardPage.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    unionPosition: PropTypes.string,
    company: PropTypes.string,
    membershipNo: PropTypes.string,
    membershipDate: PropTypes.string,
    email: PropTypes.string,
    digitalId: PropTypes.string,
    qrCode: PropTypes.string,
    qrReference: PropTypes.string,
    isApproved: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }).isRequired,
  dues: PropTypes.arrayOf(
    PropTypes.shape({
      billingPeriod: PropTypes.string,
      amount: PropTypes.number,
      status: PropTypes.string,
      dueDate: PropTypes.string,
    }),
  ).isRequired,
  onLogout: PropTypes.func.isRequired,
};
